import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { 
  HomeIcon, 
  CalendarIcon, 
  UsersIcon, 
  PlusIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  ClockIcon,
  ChartBarSquareIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { ChatProvider } from '../contexts/ChatContext';
import CustomerAssistantChat from './CustomerAssistantChat';
import GlobalSearch from './GlobalSearch';

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { customer, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if user has supervisor permissions
  const isSupervisor = customer?.role === 'supervisor' || 
                       customer?.role === 'admin' || 
                       customer?.can_view_billing_account_data;

  const baseNavigation = [
    { name: 'Dashboard', href: '/dashboard', icon: HomeIcon, color: 'blue', bgColor: 'bg-blue-50', iconColor: 'text-blue-600', hoverBg: 'hover:bg-blue-100' },
    { name: 'Appointments', href: '/appointments', icon: CalendarIcon, color: 'purple', bgColor: 'bg-purple-50', iconColor: 'text-purple-600', hoverBg: 'hover:bg-purple-100' },
    { name: 'New Appointment', href: '/appointments/new', icon: PlusIcon, color: 'green', bgColor: 'bg-green-50', iconColor: 'text-green-600', hoverBg: 'hover:bg-green-100' },
    { name: 'Claims', href: '/claims', icon: DocumentTextIcon, color: 'indigo', bgColor: 'bg-indigo-50', iconColor: 'text-indigo-600', hoverBg: 'hover:bg-indigo-100' },
    { name: 'Claimants', href: '/claimants', icon: UsersIcon, color: 'orange', bgColor: 'bg-orange-50', iconColor: 'text-orange-600', hoverBg: 'hover:bg-orange-100' },
    { name: 'Analytics', href: '/analytics', icon: ChartBarSquareIcon, color: 'indigo', bgColor: 'bg-indigo-50', iconColor: 'text-indigo-600', hoverBg: 'hover:bg-indigo-100' },
    { name: 'Service Locations', href: '/service-locations', icon: MapPinIcon, color: 'pink', bgColor: 'bg-pink-50', iconColor: 'text-pink-600', hoverBg: 'hover:bg-pink-100' },
    { name: 'Activity', href: '/activity', icon: ClockIcon, color: 'teal', bgColor: 'bg-teal-50', iconColor: 'text-teal-600', hoverBg: 'hover:bg-teal-100' },
  ];

  // Add supervisor dashboard for supervisors
  const supervisorNavigation = isSupervisor ? [
    { name: 'Supervisor Dashboard', href: '/supervisor', icon: ChartBarSquareIcon, color: 'violet', bgColor: 'bg-violet-50', iconColor: 'text-violet-600', hoverBg: 'hover:bg-violet-100' },
  ] : [];

  const navigation = [...baseNavigation, ...supervisorNavigation];

  const isActive = (href) => {
    return location.pathname === href;
  };

  const handleLogout = async () => {
    await logout();
    navigate('/auth/login');
  };

  return (
    <div className="h-screen bg-gray-50 flex">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        </div>
      )}

      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-56 bg-gradient-to-b from-white via-blue-50 to-indigo-50 shadow-lg transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-blue-100 bg-white/80 backdrop-blur-sm">
            <div className="flex items-center space-x-2">
              <div className="p-1.5 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg shadow-md">
                <BuildingOfficeIcon className="h-5 w-5 text-white" />
              </div>
              <span className="text-lg font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">Integrity</span>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-1 rounded-md text-gray-400 hover:text-gray-500"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* User Info */}
          <div className="p-4 border-b border-blue-100">
            <div className="flex items-center space-x-3">
              <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center flex-shrink-0 shadow-md">
                <span className="text-white font-semibold text-base">
                  {customer?.name?.charAt(0) || 'C'}
                </span>
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-gray-900 truncate">{customer?.name}</p>
                {customer?.role === 'supervisor' || customer?.role === 'admin' ? (
                  <span className="inline-block bg-violet-100 text-violet-800 px-2 py-0.5 rounded-full text-xs font-semibold mt-0.5">
                    {customer?.role?.toUpperCase()}
                  </span>
                ) : (
                  <p className="text-xs text-indigo-600 font-medium truncate">{customer?.email}</p>
                )}
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-1.5 overflow-y-auto">
            {navigation.map((item) => {
              const active = isActive(item.href);
              const isNewAppointment = item.href === '/appointments/new';
              
              // Special styling for New Appointment to make it prominent
              if (isNewAppointment) {
                return (
                  <button
                    key={item.name}
                    onClick={() => {
                      navigate(item.href);
                      setSidebarOpen(false);
                    }}
                    className={`w-full flex items-center px-3 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 ${
                      active
                        ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-md'
                        : 'bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-sm hover:shadow-md'
                    }`}
                  >
                    <div className="p-1 rounded-md bg-white/20 mr-3">
                      <item.icon className="h-5 w-5" />
                    </div>
                    <span>{item.name}</span>
                  </button>
                );
              }
              
              return (
                <button
                  key={item.name}
                  onClick={() => {
                    navigate(item.href);
                    setSidebarOpen(false);
                  }}
                  className={`w-full flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${
                    active
                      ? `${item.bgColor} text-${item.color}-900 shadow-sm`
                      : `text-gray-700 ${item.hoverBg}`
                  }`}
                >
                  <div className={`p-1 rounded-md mr-3 ${active ? 'bg-white/70' : 'bg-white/50'}`}>
                    <item.icon className={`h-5 w-5 ${active ? item.iconColor : `${item.iconColor} opacity-70`}`} />
                  </div>
                  <span>{item.name}</span>
                </button>
              );
            })}
          </nav>

          {/* Logout */}
          <div className="p-3 border-t border-blue-100">
            <button
              onClick={handleLogout}
              className="w-full flex items-center px-3 py-2.5 text-sm font-medium rounded-lg text-red-700 hover:bg-red-50 transition-all duration-200"
            >
              <div className="p-1 rounded-md bg-white/50 mr-3">
                <ArrowRightOnRectangleIcon className="h-5 w-5 text-red-600" />
              </div>
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </div>

      <CustomerAssistantChat />

      {/* Mobile menu button */}
      <button
        onClick={() => setSidebarOpen(true)}
        className="fixed top-4 left-4 z-30 lg:hidden p-2 rounded-md bg-white shadow-md border border-gray-200 text-gray-600 hover:text-gray-900 hover:bg-gray-50"
      >
        <Bars3Icon className="h-6 w-6" />
      </button>

      {/* Main content */}
      <div className="flex-1 pl-0 lg:pl-56 flex flex-col min-h-0 min-w-0">
        <div className="sticky top-0 z-20 flex-shrink-0 bg-white/95 backdrop-blur-sm border-b border-blue-100 pl-14 pr-4 py-3 lg:px-6">
          <GlobalSearch />
        </div>
        <main className="flex-1 min-h-0 overflow-y-auto mobile-content">
          <ChatProvider>
            <Outlet />
          </ChatProvider>
        </main>
      </div>
    </div>
  );
};

export default Layout;
